import { useState, useEffect, useRef, useCallback } from "react";

// ─────────────────────────────────────────────────────────────────────────────
// CONFIG — swap these when you're ready
// ─────────────────────────────────────────────────────────────────────────────
const CONFIG = {
  MAPBOX_TOKEN: "pk.eyJ1IjoibHhtb3JqIiwiYSI6ImNtbWd4emlubTBhNHIycm9pNGx5amEyazQifQ.LCCjoc1bZ1SOg1bt_J44nw",
  API_BASE: "/api",
  ADMIN_PIN: "676767",
  MAP_STYLE: "mapbox://styles/mapbox/dark-v11",
};

// ─────────────────────────────────────────────────────────────────────────────
// Responsive hook — returns "sm" | "md" | "lg"
// sm  < 768px   (phone)
// md  768–1100  (tablet / small laptop)
// lg  > 1100px  (desktop)
// ─────────────────────────────────────────────────────────────────────────────
function getBreakpoint(w) {
  if (w >= 1100) return "lg";
  if (w >= 768)  return "md";
  return "sm";
}
function useBreakpoint() {
  const [bp, setBp] = useState(() => getBreakpoint(window.innerWidth));
  useEffect(() => {
    const handler = () => setBp(getBreakpoint(window.innerWidth));
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return bp;
}
// Legacy alias used in some components
function useDesktop() { return useBreakpoint() !== "sm"; }

// ─────────────────────────────────────────────────────────────────────────────
// Service boundary polygon — [lng, lat] pairs
// ─────────────────────────────────────────────────────────────────────────────
const BOUNDARY = [
  [-76.10665901268209, 43.95854125923413],
  [-76.09737345970348, 43.94930342004365],
  [-76.10749696064170, 43.94060715573485],
  [-76.11057937826584, 43.93033776058171],
  [-76.12956436366657, 43.92903986697055],
  [-76.15898036830883, 43.95458855310219],
];

// Ray-casting point-in-polygon
function pointInPolygon(lng, lat, polygon) {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i], [xj, yj] = polygon[j];
    if ((yi > lat) !== (yj > lat) && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}


const STOPS = [
  { id: "sh1", name: "Marina at Madison Barracks", lat: 43.9530, lng: -76.1113, type: "landmark" },
  { id: "sh2", name: "Marina Suites",              lat: 43.9531, lng: -76.1114, type: "hotel" },
  { id: "sh3", name: "Creekside Apartments",       lat: 43.9505, lng: -76.1099, type: "landmark" },
  { id: "sh4", name: "Fort Pike Apartments",       lat: 43.9494, lng: -76.1130, type: "landmark" },
  { id: "sh5", name: "Harbor House Inn",           lat: 43.9478, lng: -76.1203, type: "hotel" },
  { id: "sh6", name: "Navy Point Marina",          lat: 43.9503, lng: -76.1223, type: "landmark" },
];

const STOP_COLOR = { hotel: "#c9a84c", restaurant: "#5ba3a0", landmark: "#e8e0d0" };

// ─────────────────────────────────────────────────────────────────────────────
// API helpers — all calls go through here; fall back to mock data if no server
// ─────────────────────────────────────────────────────────────────────────────
const MOCK_REQUESTS = [
  { id: "r1", phone: "315-555-0142", pickup: STOPS[2], dropoff: STOPS[0], status: "pending", created_at: Date.now() - 120000, party: 2 },
  { id: "r2", phone: "315-555-0887", pickup: STOPS[4], dropoff: STOPS[6], status: "pending", created_at: Date.now() - 300000, party: 1 },
];

async function apiFetch(path, options = {}) {
  try {
    const res = await fetch(`${CONFIG.API_BASE}${path}`, {
      headers: { "Content-Type": "application/json" },
      ...options,
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } catch {
    return null; // signals fallback to mock
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Fetch driving route from Mapbox Directions API
// ─────────────────────────────────────────────────────────────────────────────
async function fetchDrivingRoute(stops) {
  if (!stops || stops.length < 2) return null;
  // Build back-and-forth sequence: find turnaround stop, go forward then back
  const turnaroundIdx = stops.findIndex(s => s.turnaround === 1);
  const endIdx = turnaroundIdx >= 0 ? turnaroundIdx : stops.length - 1;
  const sequence = [
    ...stops.slice(0, endIdx + 1),
    ...stops.slice(1, endIdx).reverse(), // reverse back, skip endpoints
  ];
  // Mapbox Directions allows max 25 waypoints
  const coords = sequence.slice(0, 25).map(s => `${s.lng},${s.lat}`).join(";");
  const url = `https://api.mapbox.com/directions/v5/mapbox/driving/${coords}?geometries=geojson&overview=full&access_token=${CONFIG.MAPBOX_TOKEN}`;
  try {
    const res = await fetch(url);
    const data = await res.json();
    return data.routes?.[0]?.geometry || null;
  } catch {
    return null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Mapbox map component
// ─────────────────────────────────────────────────────────────────────────────
function MapboxMap({ stops: stopsProp, requests = [], shuttleLoc, onStopClick, selectedStop, height = "100%" }) {
  const stops = stopsProp || [];
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const shuttleMarkerRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapError, setMapError] = useState(false);

  // Init map once Mapbox GL JS is available (loaded globally by App)
  useEffect(() => {
    if (CONFIG.MAPBOX_TOKEN === "YOUR_MAPBOX_TOKEN_HERE") {
      setMapError(true);
      return;
    }
    const waitForMapbox = () => {
      if (window.mapboxgl) { initMap(); }
      else { setTimeout(waitForMapbox, 100); }
    };
    waitForMapbox();
  }, []);

  const initMap = () => {
    if (!containerRef.current || mapRef.current) return;
    const mapboxgl = window.mapboxgl;
    mapboxgl.accessToken = CONFIG.MAPBOX_TOKEN;

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: CONFIG.MAP_STYLE,
      center: [-76.1160, 43.9505],
      zoom: 14.8,
      pitch: 30,
    });

    map.on("load", async () => {
      // Add route source (empty initially)
      map.addSource("route", {
        type: "geojson",
        data: { type: "Feature", geometry: { type: "LineString", coordinates: [] } }
      });
      map.addLayer({
        id: "route-line",
        type: "line",
        source: "route",
        paint: { "line-color": "#c9a84c", "line-width": 3, "line-opacity": 0.7 },
        layout: { "line-cap": "round", "line-join": "round" }
      });
      // Fetch and draw driving route
      const geometry = await fetchDrivingRoute(stops);
      if (geometry) {
        map.getSource("route").setData({ type: "Feature", geometry });
      } else {
        // Fallback to straight lines
        const coords = stops.map(s => [s.lng, s.lat]);
        map.getSource("route").setData({ type: "Feature", geometry: { type: "LineString", coordinates: coords } });
      }
      setMapLoaded(true);
    });

    mapRef.current = map;
  };

  // Update route when stops change
  useEffect(() => {
    if (!mapLoaded || !mapRef.current) return;
    const updateRoute = async () => {
      const geometry = await fetchDrivingRoute(stops);
      const source = mapRef.current.getSource("route");
      if (!source) return;
      if (geometry) {
        source.setData({ type: "Feature", geometry });
      } else {
        const coords = stops.map(s => [s.lng, s.lat]);
        source.setData({ type: "Feature", geometry: { type: "LineString", coordinates: coords } });
      }
    };
    updateRoute();
  }, [mapLoaded, stops]);

  // Add stop markers once map loads
  useEffect(() => {
    if (!mapLoaded || !mapRef.current) return;
    const mapboxgl = window.mapboxgl;

    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];

    stops.forEach(stop => {
      const isTurnaround = stop.turnaround === 1;
      const el = document.createElement("div");
      el.style.cssText = `
        width:${isTurnaround ? "18px" : "14px"};height:${isTurnaround ? "18px" : "14px"};border-radius:50%;
        background:${isTurnaround ? "#e8843a" : STOP_COLOR[stop.type] || "#e8e0d0"};
        border:2px solid #0d2136;cursor:pointer;
        box-shadow:0 0 6px ${isTurnaround ? "#e8843a" : STOP_COLOR[stop.type] || "#e8e0d0"}88;
        transition:transform 0.15s;
        display:flex;align-items:center;justify-content:center;
        font-size:10px;
      `;
      if (isTurnaround) el.textContent = "↩";
      el.addEventListener("mouseenter", () => { el.style.transform = "scale(1.4)"; });
      el.addEventListener("mouseleave", () => { el.style.transform = "scale(1)"; });
      if (onStopClick) el.addEventListener("click", () => onStopClick(stop));

      const popup = new mapboxgl.Popup({ offset: 12, closeButton: false })
        .setHTML(`<div style="font-family:serif;font-size:12px;color:#0d2136;padding:2px 4px">${stop.icon || ""} ${stop.name}${isTurnaround ? " ↩" : ""}</div>`);

      const marker = new mapboxgl.Marker(el)
        .setLngLat([stop.lng, stop.lat])
        .setPopup(popup)
        .addTo(mapRef.current);

      markersRef.current.push(marker);
    });
  }, [mapLoaded, stops, onStopClick]);

  // Shuttle marker
  useEffect(() => {
    if (!mapLoaded || !mapRef.current) return;
    const mapboxgl = window.mapboxgl;
    if (shuttleMarkerRef.current) shuttleMarkerRef.current.remove();

    if (shuttleLoc) {
      const el = document.createElement("div");
      el.style.cssText = `width:32px;height:32px;position:relative;`;
      const inner = document.createElement("div");
      inner.style.cssText = "position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);font-size:22px;line-height:1;filter:drop-shadow(0 0 6px #3ec6a0);font-family:Apple Color Emoji,Segoe UI Emoji,sans-serif;";
      inner.textContent = "🛺";
      el.appendChild(inner);
      shuttleMarkerRef.current = new mapboxgl.Marker(el)
        .setLngLat([shuttleLoc.lng, shuttleLoc.lat])
        .addTo(mapRef.current);
    }
  }, [mapLoaded, shuttleLoc]);

  // Fallback SVG map when no token yet
  if (mapError || CONFIG.MAPBOX_TOKEN === "YOUR_MAPBOX_TOKEN_HERE") {
    return <FallbackMap requests={requests} shuttlePos={shuttleLoc || { lat: 43.9505, lng: -76.1160 }} onStopClick={onStopClick} selectedStop={selectedStop} />;
  }

  return (
    <div ref={containerRef} style={{ width: "100%", height, background: "#0d2136" }} />
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Pin Picker Map — lets riders drop a custom pin within the boundary
// ─────────────────────────────────────────────────────────────────────────────
function PinPickerMap({ onPin, existingPin, label = "Drop a pin" }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const [outOfBounds, setOutOfBounds] = useState(false);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const mapboxgl = window.mapboxgl;
    if (!mapboxgl) return;
    mapboxgl.accessToken = CONFIG.MAPBOX_TOKEN;

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: CONFIG.MAP_STYLE,
      center: [-76.1280, 43.9430],
      zoom: 13.0,
    });

    map.on("load", () => {
      map.addSource("boundary", {
        type: "geojson",
        data: { type: "Feature", geometry: { type: "Polygon", coordinates: [[...BOUNDARY, BOUNDARY[0]]] } },
      });
      map.addLayer({ id: "boundary-fill", type: "fill", source: "boundary", paint: { "fill-color": "#3ec6a0", "fill-opacity": 0.08 } });
      map.addLayer({ id: "boundary-line", type: "line", source: "boundary", paint: { "line-color": "#3ec6a0", "line-width": 2, "line-dasharray": [3, 2], "line-opacity": 0.7 } });
      if (existingPin) placeMarker(map, existingPin.lng, existingPin.lat);
    });

    map.on("click", (e) => handlePin(e.lngLat.lng, e.lngLat.lat, map));
    mapRef.current = map;
    return () => { if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; } };
  }, []);

  const placeMarker = (map, lng, lat) => {
    const mapboxgl = window.mapboxgl;
    if (markerRef.current) markerRef.current.remove();
    const el = document.createElement("div");
    el.style.cssText = "width:32px;height:32px;position:relative;cursor:grab;";
    const inner = document.createElement("div");
    inner.style.cssText = "position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);font-size:28px;line-height:1;filter:drop-shadow(0 2px 6px #000a);font-family:Apple Color Emoji,Segoe UI Emoji,sans-serif;";
    inner.textContent = "📍";
    el.appendChild(inner);
    markerRef.current = new mapboxgl.Marker({ element: el, draggable: true })
      .setLngLat([lng, lat]).addTo(map);
    markerRef.current.on("dragend", () => {
      const p = markerRef.current.getLngLat();
      handlePin(p.lng, p.lat);
    });
  };

  const handlePin = (lng, lat, map) => {
    const valid = pointInPolygon(lng, lat, BOUNDARY);
    setOutOfBounds(!valid);
    if (!valid) return;
    const m = map || mapRef.current;
    if (m) placeMarker(m, lng, lat);
    onPin({ lat, lng });
  };

  return (
    <div>
      <div style={{ fontSize: "12px", color: "#9ab8c8", marginBottom: "6px" }}>
        Tap anywhere inside the <span style={{ color: "#3ec6a0" }}>green boundary</span> to drop your pin · drag to adjust
      </div>
      <div ref={containerRef} style={{ width: "100%", height: "220px", borderRadius: "10px", overflow: "hidden", border: `1px solid ${outOfBounds ? "#e8843a" : "#3ec6a044"}` }} />
      {outOfBounds && (
        <div style={{ fontSize: "12px", color: "#e8843a", marginTop: "6px" }}>
          ⚠️ Outside our service area — please pick a spot inside the green boundary
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SVG fallback (shown until Mapbox token is added)
// ─────────────────────────────────────────────────────────────────────────────
function FallbackMap({ requests = [], shuttlePos, onStopClick, selectedStop }) {
  const mapW = 480, mapH = 300;
  const lats = STOPS.map(s => s.lat), lngs = STOPS.map(s => s.lng);
  const [minLat, maxLat] = [Math.min(...lats), Math.max(...lats)];
  const [minLng, maxLng] = [Math.min(...lngs), Math.max(...lngs)];
  const pad = 44;
  const toX = lng => pad + ((lng - minLng) / (maxLng - minLng || 1)) * (mapW - pad * 2);
  const toY = lat => (mapH - pad) - ((lat - minLat) / (maxLat - minLat || 1)) * (mapH - pad * 2);
  const loop = STOPS.map((s, i) => `${i === 0 ? "M" : "L"}${toX(s.lng)},${toY(s.lat)}`).join(" ") + "Z";

  return (
    <svg viewBox={`0 0 ${mapW} ${mapH}`} style={{ width: "100%", height: "100%", display: "block" }}>
      <defs>
        <pattern id="wv" x="0" y="0" width="20" height="10" patternUnits="userSpaceOnUse">
          <path d="M0 5 Q5 0 10 5 Q15 10 20 5" stroke="#1a3a5c22" strokeWidth="1" fill="none" />
        </pattern>
        <filter id="gl"><feGaussianBlur stdDeviation="2" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
      </defs>
      <rect width={mapW} height={mapH} fill="#0d2136"/>
      <rect width={mapW} height={mapH} fill="url(#wv)"/>
      <text x="10" y="18" fill="#1a3a5c88" fontFamily="serif" fontSize="10" fontStyle="italic">Lake Ontario ↑</text>
      <text x={mapW/2} y={mapH/2} fill="#c9a84c22" fontFamily="serif" fontSize="11" textAnchor="middle">Add Mapbox token for real map</text>
      <path d={loop} fill="none" stroke="#c9a84c44" strokeWidth="2" strokeDasharray="6 4"/>
      {requests.filter(r => r.status === "pending").map(r => (
        <line key={r.id} x1={toX(r.pickup.lng)} y1={toY(r.pickup.lat)} x2={toX(r.dropoff.lng)} y2={toY(r.dropoff.lat)} stroke="#e8843a88" strokeWidth="1.5" strokeDasharray="4 3"/>
      ))}
      {STOPS.map(s => {
        const x = toX(s.lng), y = toY(s.lat);
        const sel = selectedStop?.id === s.id;
        const pend = requests.some(r => r.status === "pending" && (r.pickup.id === s.id || r.dropoff.id === s.id));
        return (
          <g key={s.id} style={{ cursor: onStopClick ? "pointer" : "default" }} onClick={() => onStopClick?.(s)}>
            {pend && <circle cx={x} cy={y} r="12" fill="#e8843a" opacity="0.15"><animate attributeName="r" values="12;18;12" dur="2s" repeatCount="indefinite"/></circle>}
            {sel && <circle cx={x} cy={y} r="13" fill={STOP_COLOR[s.type]} opacity="0.2"/>}
            <circle cx={x} cy={y} r="7" fill={STOP_COLOR[s.type]} stroke={sel ? "#fff" : "#0d2136"} strokeWidth={sel ? 2 : 1} filter="url(#gl)"/>
            <text x={x} y={y - 10} fill="#e8e0d088" fontSize="8" textAnchor="middle" fontFamily="serif">{s.name.split(" ").slice(0,2).join(" ")}</text>
          </g>
        );
      })}
      {shuttlePos && (
        <g filter="url(#gl)">
          <circle cx={toX(shuttlePos.lng)} cy={toY(shuttlePos.lat)} r="12" fill="#3ec6a0" opacity="0.2"><animate attributeName="r" values="12;18;12" dur="1.5s" repeatCount="indefinite"/></circle>
          <text x={toX(shuttlePos.lng)} y={toY(shuttlePos.lat)+5} fontSize="14" textAnchor="middle">🛺</text>
        </g>
      )}
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared styles
// ─────────────────────────────────────────────────────────────────────────────
const S = {
  btn: (bg, color, small) => ({
    width: small ? "auto" : "100%", padding: small ? "8px 16px" : "14px",
    background: bg, color, border: "none", borderRadius: "8px",
    fontFamily: "'Source Serif 4', serif", fontSize: small ? "13px" : "15px",
    fontWeight: 600, cursor: "pointer", display: "block", transition: "opacity 0.15s",
  }),
  card: (accent) => ({
    background: "#0d2136", border: `1px solid ${accent || "#c9a84c33"}`,
    borderRadius: "10px", padding: "14px",
  }),
  label: { fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.12em", color: "#5ba3a0" },
};

// ─────────────────────────────────────────────────────────────────────────────
// Public: Ride Request Flow
// ─────────────────────────────────────────────────────────────────────────────
function PublicView({ requests, stops, shuttleLoc, onSubmit }) {
  const bp = useBreakpoint();
  const isDesktop = bp !== "sm";
  const [step, setStep] = useState("home");
  const [pickup, setPickup] = useState(null);
  const [dropoff, setDropoff] = useState(null);
  const [phone, setPhone] = useState("");
  const [smsOk, setSmsOk] = useState(false);
  const [party, setParty] = useState(1);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [pickupMode, setPickupMode] = useState("stop");
  const [dropoffMode, setDropoffMode] = useState("stop");
  const [pickupPin, setPickupPin] = useState(null);
  const [dropoffPin, setDropoffPin] = useState(null);
  const [pickupDesc, setPickupDesc] = useState("");
  const [dropoffDesc, setDropoffDesc] = useState("");
  const pending = requests.filter(r => r.status === "pending").length;

  const submit = async () => {
    if (!smsOk) { setErr("Please agree to receive ride text messages to continue"); return; }
    if (!/^\d{10}$/.test(phone.replace(/\D/g, ""))) { setErr("Enter a valid 10-digit number"); return; }
    setLoading(true);
    await onSubmit({ pickup, dropoff, phone, party });
    setLoading(false);
    setStep("submitted");
  };

  const reset = () => { setStep("home"); setPickup(null); setDropoff(null); setPhone(""); setParty(1); setErr(""); setPickupPin(null); setDropoffPin(null); setPickupDesc(""); setDropoffDesc(""); setPickupMode("stop"); setDropoffMode("stop"); };

  const panelWidth = bp === "lg" ? "460px" : bp === "md" ? "380px" : "100%";

  const header = (
    <header style={{ background: "linear-gradient(180deg,#0d2136,#0b1e30)", borderBottom: "1px solid #c9a84c44", padding: bp === "sm" ? "18px 22px 14px" : "20px 32px 16px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <span style={{ fontSize: "26px" }}>🛺</span>
        <div>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: bp === "sm" ? "21px" : "24px", fontWeight: 900, color: "#c9a84c", lineHeight: 1 }}>Harbor Shuttle</div>
          <div style={{ fontSize: "10px", letterSpacing: "0.16em", color: "#5ba3a0", textTransform: "uppercase", marginTop: "2px" }}>Sackets Harbor, NY</div>
        </div>
        <div style={{ marginLeft: "auto", textAlign: "right" }}>
          <div style={{ fontSize: "10px", color: "#5ba3a0" }}>Shuttle</div>
          <div style={{ color: "#3ec6a0", fontSize: "13px", fontWeight: 600 }}>● Active</div>
        </div>
      </div>
    </header>
  );

  const formBody = (
    <div style={{ padding: bp === "sm" ? "20px 22px" : "24px 32px" }}>
        {step === "home" && <>
          {pending > 0 && (
            <div style={{ ...S.card("#e8843a44"), marginBottom: "14px", fontSize: "13px", color: "#e8a87c" }}>
              🕐 {pending} ride{pending > 1 ? "s" : ""} currently in queue
            </div>
          )}
          <p style={{ fontSize: "13px", color: "#9ab8c8", margin: "0 0 16px" }}>
            The shuttle loops through the village continuously. Request a pickup at any stop.
          </p>
          <button onClick={() => setStep("pickup")} style={S.btn("#c9a84c", "#0b1e30")}>Request a Ride</button>
          <div style={{ marginTop: "22px" }}>
            <div style={{ ...S.label, marginBottom: "10px" }}>Stops on the loop</div>
            {stops.map(s => (
              <div key={s.id} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "8px 0", borderBottom: "1px solid #ffffff08", fontSize: "13px" }}>
                <span style={{ fontSize: "15px" }}>{s.icon || "📍"}</span>
                <span>{s.name}</span>
                <span style={{ marginLeft: "auto", fontSize: "11px", color: "#5ba3a055", textTransform: "capitalize" }}>{s.type}</span>
              </div>
            ))}
          </div>
        </>}

        {(step === "pickup" || step === "dropoff") && <>
          <div style={{ marginBottom: "12px" }}>
            <div style={{ fontSize: "13px", color: "#c9a84c", fontWeight: 600, marginBottom: "3px" }}>
              {step === "pickup" ? "📍 Where should we pick you up?" : "🎯 Where are you headed?"}
            </div>
          </div>

          {/* Mode toggle */}
          <div style={{ display: "flex", gap: "8px", marginBottom: "14px" }}>
            {[["stop", "🚏 Pick a stop"], ["pin", "📍 Drop a pin"]].map(([mode, label]) => (
              <button key={mode}
                onClick={() => step === "pickup" ? setPickupMode(mode) : setDropoffMode(mode)}
                style={{ flex: 1, padding: "9px", borderRadius: "8px", border: "1px solid #c9a84c44", background: (step === "pickup" ? pickupMode : dropoffMode) === mode ? "#c9a84c22" : "#0d2136", color: (step === "pickup" ? pickupMode : dropoffMode) === mode ? "#c9a84c" : "#9ab8c8", fontFamily: "inherit", fontSize: "13px", cursor: "pointer" }}>
                {label}
              </button>
            ))}
          </div>

          {/* Stop list */}
          {(step === "pickup" ? pickupMode : dropoffMode) === "stop" && (
            <div style={{ display: "flex", flexDirection: "column", gap: "7px", marginBottom: "14px" }}>
              {stops.filter(s => step === "dropoff" ? s.id !== pickup?.id : true).map(s => (
                <button key={s.id}
                  onClick={() => {
                    if (step === "pickup") { setPickup(s); setStep("dropoff"); }
                    else { setDropoff(s); setStep("phone"); }
                  }}
                  style={{ background: "#0d2136", border: "1px solid #c9a84c33", borderRadius: "8px", padding: "12px 14px", color: "#e8e0d0", fontFamily: "inherit", fontSize: "14px", textAlign: "left", cursor: "pointer", display: "flex", alignItems: "center", gap: "10px" }}>
                  <span style={{ fontSize: "15px" }}>{s.icon || "📍"}</span>
                  {s.name}
                </button>
              ))}
            </div>
          )}

          {/* Pin drop */}
          {(step === "pickup" ? pickupMode : dropoffMode) === "pin" && (
            <div style={{ marginBottom: "14px" }}>
              <PinPickerMap
                onPin={(coords) => step === "pickup" ? setPickupPin(coords) : setDropoffPin(coords)}
                existingPin={step === "pickup" ? pickupPin : dropoffPin}
              />
              {/* Description input */}
              <div style={{ marginTop: "10px" }}>
                <input
                  type="text"
                  placeholder={step === "pickup" ? "Describe your location (e.g. Blue house on Main St)" : "Describe your destination"}
                  value={step === "pickup" ? pickupDesc : dropoffDesc}
                  onChange={e => step === "pickup" ? setPickupDesc(e.target.value) : setDropoffDesc(e.target.value)}
                  style={{ width: "100%", background: "#0d2136", border: "1px solid #c9a84c44", borderRadius: "8px", padding: "11px 14px", color: "#e8e0d0", fontFamily: "inherit", fontSize: "13px", boxSizing: "border-box" }}
                />
              </div>
              <button
                disabled={!(step === "pickup" ? pickupPin && pickupDesc.trim() : dropoffPin && dropoffDesc.trim())}
                onClick={() => {
                  if (step === "pickup") {
                    setPickup({ id: `pin-pickup`, name: pickupDesc.trim(), lat: pickupPin.lat, lng: pickupPin.lng, isPin: true });
                    setStep("dropoff");
                  } else {
                    setDropoff({ id: `pin-dropoff`, name: dropoffDesc.trim(), lat: dropoffPin.lat, lng: dropoffPin.lng, isPin: true });
                    setStep("phone");
                  }
                }}
                style={{ ...S.btn("#c9a84c", "#0b1e30"), marginTop: "10px", opacity: (step === "pickup" ? pickupPin && pickupDesc.trim() : dropoffPin && dropoffDesc.trim()) ? 1 : 0.4 }}>
                Confirm {step === "pickup" ? "Pickup" : "Destination"} →
              </button>
            </div>
          )}

          <button onClick={() => setStep(step === "pickup" ? "home" : "pickup")} style={S.btn("#ffffff11", "#9ab8c8")}>← Back</button>
        </>}

        {step === "phone" && <>
          <div style={{ ...S.card(), marginBottom: "14px" }}>
            <div style={{ fontSize: "12px", color: "#9ab8c8", marginBottom: "3px" }}>📍 Pickup</div>
            <div style={{ marginBottom: "10px" }}>{pickup?.name}</div>
            <div style={{ fontSize: "12px", color: "#9ab8c8", marginBottom: "3px" }}>🎯 Drop-off</div>
            <div>{dropoff?.name}</div>
          </div>
          <div style={{ marginBottom: "14px" }}>
            <div style={{ ...S.label, marginBottom: "8px" }}>Party size</div>
            <div style={{ display: "flex", gap: "8px" }}>
              {[1,2,3,4,5,6].map(n => (
                <button key={n} onClick={() => setParty(n)} style={{ width: "36px", height: "36px", borderRadius: "50%", background: party === n ? "#c9a84c" : "#0d2136", border: "1px solid #c9a84c44", color: party === n ? "#0b1e30" : "#e8e0d0", fontFamily: "inherit", fontSize: "14px", cursor: "pointer", fontWeight: 600 }}>{n}</button>
              ))}
            </div>
          </div>
          <div style={{ marginBottom: "14px" }}>
            <div style={{ ...S.label, marginBottom: "6px" }}>Your phone number</div>
            <input type="tel" placeholder="(315) 555-0100" value={phone} onChange={e => { setPhone(e.target.value); setErr(""); }}
              style={{ width: "100%", background: "#0d2136", border: `1px solid ${err ? "#e8843a" : "#c9a84c44"}`, borderRadius: "8px", padding: "12px 14px", color: "#e8e0d0", fontFamily: "inherit", fontSize: "14px", boxSizing: "border-box" }} />
            {err && <div style={{ fontSize: "11px", color: "#e8843a", marginTop: "4px" }}>{err}</div>}
            <div style={{ fontSize: "11px", color: "#9ab8c855", marginTop: "4px" }}>You'll get a text when the shuttle is close</div>
          </div>
          <label style={{ display: "flex", alignItems: "flex-start", gap: "9px", margin: "0 0 14px", cursor: "pointer" }}>
            <input type="checkbox" checked={smsOk} onChange={e => { setSmsOk(e.target.checked); setErr(""); }}
              style={{ marginTop: "3px", width: "16px", height: "16px", accentColor: "#c9a84c", flexShrink: 0 }} />
            <span style={{ fontSize: "11px", lineHeight: 1.5, color: "#9ab8c8" }}>
              By requesting a ride, I agree to receive transactional SMS about my ride from Harbor Shuttle via Twilio. Msg &amp; data rates may apply. Msg frequency varies. Reply STOP to opt out, HELP for help. See our{" "}
              <a href="https://privacy.sacketshotels.com" target="_blank" rel="noopener noreferrer" style={{ color: "#c9a84c" }}>Privacy Policy</a>{" "}and{" "}
              <a href="https://terms.sacketshotels.com" target="_blank" rel="noopener noreferrer" style={{ color: "#c9a84c" }}>Terms</a>.
            </span>
          </label>
          <button onClick={submit} disabled={loading || !smsOk} style={{ ...S.btn("#c9a84c", "#0b1e30"), opacity: smsOk ? 1 : 0.5, cursor: smsOk ? "pointer" : "not-allowed" }}>{loading ? "Requesting…" : "Request Pickup"}</button>
          <button onClick={() => setStep("dropoff")} style={{ ...S.btn("#ffffff11", "#9ab8c8"), marginTop: "8px" }}>← Back</button>
        </>}

        {step === "submitted" && (
          <div style={{ textAlign: "center", padding: "24px 0" }}>
            <div style={{ fontSize: "52px", marginBottom: "10px" }}>🛺</div>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: "24px", color: "#c9a84c", marginBottom: "8px" }}>You're on the list!</div>
            <div style={{ fontSize: "13px", color: "#9ab8c8", marginBottom: "20px" }}>We'll text {phone} when the shuttle is on its way.</div>
            <div style={{ ...S.card(), fontSize: "13px", marginBottom: "20px", textAlign: "left" }}>
              <div style={{ color: "#9ab8c8", marginBottom: "3px" }}>Pickup · {pickup?.name}</div>
              <div style={{ color: "#c9a84c" }}>Drop-off · {dropoff?.name}</div>
              <div style={{ color: "#9ab8c866", marginTop: "6px" }}>Party of {party}</div>
            </div>
            <button onClick={reset} style={S.btn("#c9a84c", "#0b1e30")}>Done</button>
          </div>
        )}
      </div>
  );

  if (isDesktop) {
    return (
      <div style={{ minHeight: "100vh", background: "#0b1e30", color: "#e8e0d0", fontFamily: "'Source Serif 4', serif", display: "flex", flexDirection: "column" }}>
        {header}
        <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
          {/* Left: map fills remaining space */}
          <div style={{ flex: 1, position: "relative", minWidth: 0 }}>
            <MapboxMap stops={stops} requests={requests} shuttleLoc={shuttleLoc} height="100%" />
          </div>
          {/* Right: form panel, scrollable, scales with breakpoint */}
          <div style={{ width: panelWidth, flexShrink: 0, overflowY: "auto", borderLeft: "1px solid #c9a84c22", background: "#0b1e30" }}>
            {formBody}
          </div>
        </div>
      </div>
    );
  }

  // Mobile: stacked layout with viewport-proportional map
  const mobileMapH = Math.max(200, Math.round(window.innerHeight * 0.28));
  return (
    <div style={{ minHeight: "100vh", background: "#0b1e30", color: "#e8e0d0", fontFamily: "'Source Serif 4', serif" }}>
      {header}
      <div style={{ height: `${mobileMapH}px`, borderBottom: "1px solid #c9a84c22" }}>
        <MapboxMap stops={stops} requests={requests} shuttleLoc={shuttleLoc} height={`${mobileMapH}px`} />
      </div>
      {formBody}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Route helpers — compute back-and-forth sequence and nearest stop
// ─────────────────────────────────────────────────────────────────────────────
function buildRouteSequence(stops) {
  if (!stops?.length) return [];
  const turnaroundIdx = stops.findIndex(s => s.turnaround === 1);
  const endIdx = turnaroundIdx >= 0 ? turnaroundIdx : stops.length - 1;
  return [
    ...stops.slice(0, endIdx + 1),
    ...stops.slice(1, endIdx).reverse(),
  ];
}

function distanceBetween(a, b) {
  if (!a || !b) return Infinity;
  const R = 6371000;
  const dLat = (b.lat - a.lat) * Math.PI / 180;
  const dLng = (b.lng - a.lng) * Math.PI / 180;
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * Math.PI / 180) * Math.cos(b.lat * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
}

function getNextStops(stops, shuttleLoc, count = 2) {
  if (!stops?.length || !shuttleLoc) return [];
  const seq = buildRouteSequence(stops);
  // Find index of closest stop in sequence
  let closestIdx = 0, minDist = Infinity;
  seq.forEach((s, i) => {
    const d = distanceBetween(shuttleLoc, s);
    if (d < minDist) { minDist = d; closestIdx = i; }
  });
  // Return next `count` stops after closest
  const next = [];
  for (let i = 1; i <= count; i++) {
    next.push(seq[(closestIdx + i) % seq.length]);
  }
  return next;
}

// ─────────────────────────────────────────────────────────────────────────────
// Driver Map
// ─────────────────────────────────────────────────────────────────────────────
function DriverMap({ stops, requests, shuttleLoc, nextStops, height = "200px" }) {
  const containerRef = useRef(null);
  const mapRef       = useRef(null);
  const shuttleRef   = useRef(null);   // single 🛺 marker
  const nextRefs     = useRef([null, null]); // next-stop dot markers
  const pinRefs      = useRef({});     // request id → 📍 marker
  const [mapLoaded, setMapLoaded] = useState(false);

  // ── Init map + sources/layers once ────────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const init = () => {
      if (!window.mapboxgl) { setTimeout(init, 200); return; }
      const mgl = window.mapboxgl;
      mgl.accessToken = CONFIG.MAPBOX_TOKEN;
      const map = new mgl.Map({
        container: containerRef.current,
        style: CONFIG.MAP_STYLE,
        center: [-76.1160, 43.9505],
        zoom: 14.5,
      });
      map.on("load", async () => {
        map.addSource("full-route", { type: "geojson", data: { type: "Feature", geometry: { type: "LineString", coordinates: [] } } });
        map.addLayer({ id: "full-route-line", type: "line", source: "full-route", layout: { "line-cap": "round", "line-join": "round" }, paint: { "line-color": "#c9a84c", "line-width": 2, "line-opacity": 0.45, "line-dasharray": [2, 3] } });
        map.addSource("seg0", { type: "geojson", data: { type: "Feature", geometry: { type: "LineString", coordinates: [] } } });
        map.addLayer({ id: "seg0-line", type: "line", source: "seg0", layout: { "line-cap": "round", "line-join": "round" }, paint: { "line-color": "#3ec6a0", "line-width": 4, "line-opacity": 1 } });
        map.addSource("seg1", { type: "geojson", data: { type: "Feature", geometry: { type: "LineString", coordinates: [] } } });
        map.addLayer({ id: "seg1-line", type: "line", source: "seg1", layout: { "line-cap": "round", "line-join": "round" }, paint: { "line-color": "#2a7a70", "line-width": 3, "line-opacity": 0.75 } });
        const fullGeom = await fetchDrivingRoute(stops);
        if (fullGeom) map.getSource("full-route").setData({ type: "Feature", geometry: fullGeom });
        mapRef.current = map;
        setMapLoaded(true);
      });
    };
    init();
    return () => {
      shuttleRef.current?.remove();   shuttleRef.current = null;
      nextRefs.current.forEach(m => m?.remove()); nextRefs.current = [null, null];
      Object.values(pinRefs.current).forEach(m => m.remove()); pinRefs.current = {};
      mapRef.current?.remove(); mapRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Shuttle marker — create once with real coords, then just move it ───────
  useEffect(() => {
    if (!mapLoaded || !mapRef.current || !shuttleLoc) return;
    const mgl = window.mapboxgl;
    if (!shuttleRef.current) {
      const el = document.createElement("div");
      el.style.cssText = "width:32px;height:32px;position:relative;";
      const inner = document.createElement("div");
      inner.style.cssText = "position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);font-size:24px;line-height:1;filter:drop-shadow(0 0 8px #3ec6a0aa);font-family:Apple Color Emoji,Segoe UI Emoji,sans-serif;";
      inner.textContent = "🛺";
      el.appendChild(inner);
      shuttleRef.current = new mgl.Marker({ element: el })
        .setLngLat([shuttleLoc.lng, shuttleLoc.lat])
        .addTo(mapRef.current);
    } else {
      shuttleRef.current.setLngLat([shuttleLoc.lng, shuttleLoc.lat]);
    }
    mapRef.current.easeTo({ center: [shuttleLoc.lng, shuttleLoc.lat], duration: 600 });
  }, [mapLoaded, shuttleLoc?.lat, shuttleLoc?.lng]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Next-stop dot markers — create with real coords, move or remove ────────
  useEffect(() => {
    if (!mapLoaded || !mapRef.current) return;
    const mgl = window.mapboxgl;
    [0, 1].forEach(i => {
      const stop = nextStops[i];
      if (!stop) {
        nextRefs.current[i]?.remove();
        nextRefs.current[i] = null;
        return;
      }
      if (!nextRefs.current[i]) {
        const el = document.createElement("div");
        el.style.cssText = i === 0
          ? "width:18px;height:18px;border-radius:50%;background:#3ec6a0;box-shadow:0 0 10px #3ec6a088;border:2px solid #081828;"
          : "width:13px;height:13px;border-radius:50%;background:#3ec6a055;border:2px solid #081828;";
        const popup = new mgl.Popup({ offset: 12, closeButton: false })
          .setHTML(`<div style="font-size:11px;font-family:serif;padding:2px 5px;color:#0d2136">${i === 0 ? "▶ " : ""}${stop.name}</div>`);
        nextRefs.current[i] = new mgl.Marker({ element: el })
          .setLngLat([stop.lng, stop.lat])
          .setPopup(popup)
          .addTo(mapRef.current);
      } else {
        nextRefs.current[i].setLngLat([stop.lng, stop.lat]);
        nextRefs.current[i].getPopup().setHTML(`<div style="font-size:11px;font-family:serif;padding:2px 5px;color:#0d2136">${i === 0 ? "▶ " : ""}${stop.name}</div>`);
      }
    });
  }, [mapLoaded, nextStops.map(s => s?.id).join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Pin markers — add new, remove stale ───────────────────────────────────
  useEffect(() => {
    if (!mapLoaded || !mapRef.current) return;
    const mgl = window.mapboxgl;
    const activeIds = new Set();
    requests
      .filter(r => r.status !== "completed" && r.status !== "cancelled" && r.pickup?.isPin && r.pickup?.lat != null)
      .forEach(r => {
        activeIds.add(r.id);
        if (!pinRefs.current[r.id]) {
          const el = document.createElement("div");
          el.style.cssText = "width:16px;height:16px;border-radius:50%;background:#c9a84c;border:3px solid #fff;box-shadow:0 0 0 2px #c9a84c,0 2px 6px #000a;";
          const popup = new mgl.Popup({ offset: 12, closeButton: false })
            .setHTML(`<div style="font-size:11px;font-family:serif;padding:2px 5px;color:#0d2136">${r.pickup.name} · ${r.party} pax</div>`);
          pinRefs.current[r.id] = new mgl.Marker({ element: el })
            .setLngLat([r.pickup.lng, r.pickup.lat])
            .setPopup(popup)
            .addTo(mapRef.current);
        }
      });
    Object.keys(pinRefs.current).forEach(id => {
      if (!activeIds.has(id)) { pinRefs.current[id].remove(); delete pinRefs.current[id]; }
    });
  }, [mapLoaded, requests.map(r => r.id + r.status).join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Segmented route: seg0 = shuttle→dest1 (bright), seg1 = dest1→dest2 (dim) ─
  useEffect(() => {
    if (!mapLoaded || !mapRef.current || !shuttleLoc || nextStops.length === 0) return;
    const dest1 = nextStops[0];
    const dest2 = nextStops[1];

    const fetchSeg = async (pts, sourceId) => {
      const coordStr = pts.map(p => `${p.lng},${p.lat}`).join(";");
      try {
        const r = await fetch(`https://api.mapbox.com/directions/v5/mapbox/driving/${coordStr}?geometries=geojson&overview=full&access_token=${CONFIG.MAPBOX_TOKEN}`);
        const data = await r.json();
        const geom = data.routes?.[0]?.geometry || { type: "LineString", coordinates: pts.map(p => [p.lng, p.lat]) };
        mapRef.current?.getSource(sourceId)?.setData({ type: "Feature", geometry: geom });
      } catch {
        mapRef.current?.getSource(sourceId)?.setData({ type: "Feature", geometry: { type: "LineString", coordinates: pts.map(p => [p.lng, p.lat]) } });
      }
    };

    fetchSeg([shuttleLoc, dest1], "seg0");
    if (dest2) {
      fetchSeg([dest1, dest2], "seg1");
    } else {
      mapRef.current?.getSource("seg1")?.setData({ type: "Feature", geometry: { type: "LineString", coordinates: [] } });
    }
  }, [mapLoaded, shuttleLoc?.lat, shuttleLoc?.lng, nextStops.map(s => s?.id).join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  return <div ref={containerRef} style={{ width: "100%", height, background: "#0d2136" }} />;
}

// ─────────────────────────────────────────────────────────────────────────────
// Driver View
// ─────────────────────────────────────────────────────────────────────────────
function DriverView({ requests, stops, shuttleLoc, onUpdate, onBroadcastLocation, driverName }) {
  const active   = requests.find(r => r.status === "active");
  const pending  = requests.filter(r => r.status === "pending");
  const completed = requests.filter(r => r.status === "completed");
  const [broadcasting, setBroadcasting] = useState(false);
  const dragItem = useRef(null);
  const dragOver = useRef(null);

  // Build driver sequence: nearest stop first, then forward, no ping-pong duplicates.
  // Active pin rides are inserted after position 0 so driver can see/reorder them.
  const buildSeq = () => {
    if (!stops?.length) return [];
    let nearestIdx = 0, minDist = Infinity;
    if (shuttleLoc) {
      stops.forEach((s, i) => {
        const d = distanceBetween(shuttleLoc, s);
        if (d < minDist) { minDist = d; nearestIdx = i; }
      });
    }
    const rotated = [...stops.slice(nearestIdx), ...stops.slice(0, nearestIdx)];
    // Only insert ACTIVE pin rides into the sequence (not pending — those show in the pending list below)
    const activePins = requests
      .filter(r => r.status === "active" && r.pickup?.isPin)
      .map(r => ({ ...r.pickup, id: `pin-${r.id}`, requestId: r.id, isCustomPin: true, phone: r.phone, party: r.party }));
    activePins.forEach((pin, i) => rotated.splice(1 + i, 0, pin));
    return rotated;
  };

  const [routeSequence, setRouteSequence] = useState(() => buildSeq());

  // Rebuild when stops/pending changes, preserving any driver-imposed order
  useEffect(() => {
    setRouteSequence(prev => {
      const newSeq = buildSeq();
      // Keep existing order for items already in prev; only add new pins
      const prevIds = new Set(prev.map(s => s.id));
      const additions = newSeq.filter(s => !prevIds.has(s.id));
      const updated = prev
        .map(s => newSeq.find(n => n.id === s.id) ?? s) // refresh stop data
        .filter(s => newSeq.find(n => n.id === s.id));  // remove stale pins
      additions.forEach((pin, i) => updated.splice(1 + i, 0, pin));
      return updated.length ? updated : newSeq;
    });
  }, [stops?.length, requests.filter(r=>r.status==="pending").length, requests.filter(r=>r.status==="active").length]);

  const nextStops = routeSequence.slice(1, 3);

  const toggleBroadcast = () => {
    setBroadcasting(b => { onBroadcastLocation(!b); return !b; });
  };

  const onDragStart = i => { dragItem.current = i; };
  const onDragEnter = i => { dragOver.current = i; };
  const onDragEnd   = async () => {
    const from = dragItem.current, to = dragOver.current;
    dragItem.current = null; dragOver.current = null;
    if (from === null || to === null || from === to) return;
    const reordered = [...routeSequence];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    setRouteSequence(reordered);
    // Persist regular stop order
    const regularStops = reordered.filter(s => !s.isCustomPin);
    await Promise.all(regularStops.map((s, i) =>
      apiFetch(`/stops/${s.id}`, { method: "PATCH", body: JSON.stringify({ sort_order: i + 1 }) })
    ));
    // Persist custom pin positions
    for (let i = 0; i < reordered.length; i++) {
      const s = reordered[i];
      if (!s.isCustomPin) continue;
      const prev = reordered.slice(0, i).filter(x => !x.isCustomPin).slice(-1)[0];
      const next = reordered.slice(i + 1).filter(x => !x.isCustomPin)[0];
      const prevOrd = prev?.sort_order ?? 0;
      const nextOrd = next?.sort_order ?? prevOrd + 2;
      await apiFetch(`/requests/${s.requestId}`, { method: "PATCH", body: JSON.stringify({ pickup_sort_order: (prevOrd + nextOrd) / 2 }) });
    }
  };

  const isDesktop = useDesktop();
  const bp = useBreakpoint();
  const driverPanelWidth = bp === "lg" ? "420px" : "340px";
  const mobileMapH = Math.max(180, Math.round(window.innerHeight * 0.28));

  // Queue content — shared between mobile and desktop sidebar
  const queueContent = (
    <>
      {/* Active ride */}
      {active ? (
        <div style={{ ...S.card("#3ec6a033"), marginBottom: "11px" }}>
          <div style={{ ...S.label, color: "#3ec6a0", marginBottom: "5px" }}>Active Ride</div>
          <div style={{ fontSize: "13px", marginBottom: "2px" }}>📍 {active.pickup.name}</div>
          <div style={{ fontSize: "13px", marginBottom: "5px" }}>🎯 {active.dropoff.name}</div>
          <div style={{ fontSize: "11px", color: "#9ab8c8", marginBottom: "9px" }}>Party of {active.party} · {active.phone}</div>
          <button onClick={() => onUpdate(active.id, "completed")} style={S.btn("#3ec6a0", "#081828")}>Mark Complete ✓</button>
        </div>
      ) : (
        <div style={{ border: "1px dashed #c9a84c22", borderRadius: "8px", padding: "8px 12px", marginBottom: "11px", textAlign: "center", fontSize: "12px", color: "#c9a84c44" }}>
          No active ride
        </div>
      )}
      {/* Pending requests */}
      {pending.length > 0 && (
        <div style={{ marginBottom: "11px" }}>
          <div style={{ ...S.label, marginBottom: "8px" }}>Pending ({pending.length})</div>
          {pending.map(r => (
            <div key={r.id} style={{ ...S.card("#c9a84c22"), marginBottom: "8px" }}>
              <div style={{ fontSize: "13px", marginBottom: "2px" }}>📍 {r.pickup.name}</div>
              <div style={{ fontSize: "13px", marginBottom: "5px" }}>🎯 {r.dropoff.name}</div>
              <div style={{ fontSize: "11px", color: "#9ab8c8", marginBottom: "8px" }}>Party of {r.party} · {r.phone}</div>
              <button onClick={() => onUpdate(r.id, "active")} style={S.btn("#c9a84c", "#0b1e30")}>Accept Ride</button>
            </div>
          ))}
        </div>
      )}
      {/* Route sequence */}
      <div style={{ ...S.label, marginBottom: "8px" }}>Route Sequence</div>
      <div style={{ fontSize: "11px", color: "#5ba3a066", marginBottom: "6px" }}>Drag to reorder</div>
      {routeSequence.map((s, idx) => (
        <div key={s.id}
          draggable
          onDragStart={() => onDragStart(idx)}
          onDragEnter={() => onDragEnter(idx)}
          onDragEnd={onDragEnd}
          onDragOver={e => e.preventDefault()}
          style={{ ...S.card(s.isCustomPin ? "#c9a84c33" : undefined), marginBottom: "6px", display: "flex", alignItems: "center", gap: "8px", cursor: "grab", userSelect: "none", opacity: idx === 0 ? 0.5 : 1 }}>
          <span style={{ fontSize: "13px", color: "#5ba3a055" }}>⠿</span>
          <span style={{ fontSize: "15px" }}>{s.isCustomPin ? "📍" : (s.icon || "·")}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: "12px", fontWeight: idx === 1 ? 700 : 400, color: idx === 1 ? "#3ec6a0" : "#e8e0d0" }}>{s.name}</div>
            {s.isCustomPin && <div style={{ fontSize: "10px", color: "#c9a84c88" }}>Custom pin · {s.party} pax</div>}
          </div>
          {idx === 0 && <span style={{ fontSize: "10px", color: "#5ba3a055" }}>here</span>}
          {idx === 1 && <span style={{ fontSize: "10px", color: "#3ec6a088" }}>▶ next</span>}
        </div>
      ))}
      {/* Completed */}
      {completed.length > 0 && (
        <div style={{ marginTop: "14px" }}>
          <div style={{ ...S.label, marginBottom: "8px" }}>Completed ({completed.length})</div>
          {completed.slice(-3).reverse().map(r => (
            <div key={r.id} style={{ ...S.card(), marginBottom: "6px", opacity: 0.5 }}>
              <div style={{ fontSize: "12px" }}>{r.pickup.name} → {r.dropoff.name}</div>
              <div style={{ fontSize: "11px", color: "#3ec6a0" }}>✓ {r.party} pax</div>
            </div>
          ))}
        </div>
      )}
    </>
  );

  return (
    <div style={{ minHeight: "100vh", background: "#0a1a28", color: "#e8e0d0", fontFamily: "'Source Serif 4', serif", display: "flex", flexDirection: "column" }}>

      {/* Header */}
      <div style={{ background: "#0d2136", borderBottom: "1px solid #c9a84c55", padding: "11px 14px", display: "flex", alignItems: "center", gap: "10px", boxSizing: "border-box", flexShrink: 0 }}>
        <span style={{ fontSize: "17px" }}>🛺</span>
        <span style={{ fontFamily: "'Playfair Display', serif", fontSize: "15px", fontWeight: 700, color: "#c9a84c" }}>Driver Mode</span>
        {driverName && <span style={{ fontSize: "12px", color: "#5ba3a0" }}>· {driverName}</span>}
        <button onClick={toggleBroadcast} style={{ marginLeft: "auto", background: broadcasting ? "#3ec6a022" : "#ffffff11", border: `1px solid ${broadcasting ? "#3ec6a0" : "#ffffff22"}`, borderRadius: "20px", padding: "5px 11px", color: broadcasting ? "#3ec6a0" : "#9ab8c8", fontSize: "11px", cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>
          {broadcasting ? "● GPS On" : "Start GPS"}
        </button>
      </div>

      {/* Next stops strip — always visible */}
      {nextStops.length > 0 && (
        <div style={{ background: "#081828", borderBottom: "1px solid #3ec6a018", padding: "7px 14px", display: "flex", gap: "8px", alignItems: "center", flexShrink: 0 }}>
          <span style={{ fontSize: "10px", color: "#5ba3a0", textTransform: "uppercase", letterSpacing: "0.1em", flexShrink: 0 }}>Next</span>
          {nextStops.map((s, i) => (
            <div key={s.id} style={{ display: "flex", alignItems: "center", gap: "5px" }}>
              {i > 0 && <span style={{ color: "#ffffff18" }}>→</span>}
              <span style={{ fontSize: "13px", color: i === 0 ? "#3ec6a0" : "#9ab8c8", fontWeight: i === 0 ? 600 : 400 }}>
                {s.icon || "·"} {s.name}
              </span>
            </div>
          ))}
        </div>
      )}

      {isDesktop ? (
        /* ── Tablet/Desktop: map left, queue right ── */
        <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
          <div style={{ flex: 1, position: "relative", minWidth: 0 }}>
            <DriverMap stops={stops || []} requests={requests} shuttleLoc={shuttleLoc} nextStops={nextStops} height="100%" />
          </div>
          <div style={{ width: driverPanelWidth, flexShrink: 0, overflowY: "auto", borderLeft: "1px solid #c9a84c22", background: "#0a1a28" }}>
            <div style={{ padding: "11px 13px" }}>
              {queueContent}
            </div>
          </div>
        </div>
      ) : (
        /* ── Mobile: map on top, queue below ── */
        <>
          <div style={{ height: `${mobileMapH}px`, borderBottom: "1px solid #c9a84c22", position: "relative", flexShrink: 0 }}>
            <DriverMap stops={stops || []} requests={requests} shuttleLoc={shuttleLoc} nextStops={nextStops} height={`${mobileMapH}px`} />
          </div>
          <div style={{ padding: "11px 13px", overflowY: "auto" }}>
            {queueContent}
          </div>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Stops Editor — with drag-to-reorder and turnaround toggle
// ─────────────────────────────────────────────────────────────────────────────
const ICON_OPTIONS = ["📍","⚓","🏨","🏠","🍺","🍽️","🏖️","⛵","🚢","🏛️","🌊","🛶"];
const TYPE_OPTIONS = ["landmark","hotel","restaurant","marina","apartment"];

function StopsEditor({ stops, onStopsChange }) {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [localStops, setLocalStops] = useState(stops);
  const dragItem = useRef(null);
  const dragOver = useRef(null);

  useEffect(() => { setLocalStops(stops); }, [stops]);

  const startEdit = (stop) => { setEditing(stop.id); setForm({ ...stop }); setErr(""); };
  const startNew = () => { setEditing("new"); setForm({ name: "", lat: "", lng: "", type: "landmark", icon: "📍", sort_order: stops.length + 1, active: 1, turnaround: 0 }); setErr(""); };
  const cancel = () => { setEditing(null); setForm({}); setErr(""); };

  const save = async () => {
    if (!form.name?.trim()) { setErr("Name is required"); return; }
    if (!form.lat || isNaN(parseFloat(form.lat))) { setErr("Valid latitude required"); return; }
    if (!form.lng || isNaN(parseFloat(form.lng))) { setErr("Valid longitude required"); return; }
    setSaving(true);
    const body = { ...form, lat: parseFloat(form.lat), lng: parseFloat(form.lng), sort_order: parseInt(form.sort_order) || 0 };
    const result = editing === "new"
      ? await apiFetch("/stops", { method: "POST", body: JSON.stringify(body) })
      : await apiFetch(`/stops/${editing}`, { method: "PATCH", body: JSON.stringify(body) });
    setSaving(false);
    if (result) { onStopsChange(); setEditing(null); }
    else setErr("Failed to save — check server");
  };

  const deleteStop = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    await apiFetch(`/stops/${id}`, { method: "DELETE" });
    onStopsChange();
  };

  const toggleTurnaround = async (stop) => {
    const newVal = stop.turnaround === 1 ? 0 : 1;
    await apiFetch(`/stops/${stop.id}`, { method: "PATCH", body: JSON.stringify({ turnaround: newVal }) });
    onStopsChange();
  };

  // Drag to reorder
  const onDragStart = (idx) => { dragItem.current = idx; };
  const onDragEnter = (idx) => { dragOver.current = idx; };
  const onDragEnd = async () => {
    const from = dragItem.current;
    const to = dragOver.current;
    if (from === null || to === null || from === to) { dragItem.current = null; dragOver.current = null; return; }
    const reordered = [...localStops];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    setLocalStops(reordered);
    dragItem.current = null; dragOver.current = null;
    // Persist new sort_order for all stops
    await Promise.all(reordered.map((s, i) =>
      apiFetch(`/stops/${s.id}`, { method: "PATCH", body: JSON.stringify({ sort_order: i + 1 }) })
    ));
    onStopsChange();
  };

  const inp = (field, placeholder, type = "text") => (
    <input type={type} placeholder={placeholder} value={form[field] ?? ""}
      onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
      style={{ width: "100%", background: "#08141f", border: "1px solid #c9a84c33", borderRadius: "6px", padding: "9px 10px", color: "#e8e0d0", fontFamily: "inherit", fontSize: "13px", boxSizing: "border-box" }}
    />
  );

  // Build route preview string
  const turnaroundIdx = localStops.findIndex(s => s.turnaround === 1);
  const routePreview = turnaroundIdx >= 0
    ? [...localStops.slice(0, turnaroundIdx + 1).map(s => s.name.split(" ")[0]),
       "↩",
       ...localStops.slice(1, turnaroundIdx).reverse().map(s => s.name.split(" ")[0]),
       "…loop"].join(" → ")
    : localStops.map(s => s.name.split(" ")[0]).join(" → ") + " → loop";

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
        <div style={{ ...S.label }}>Stops ({localStops.length})</div>
        <button onClick={startNew} style={{ ...S.btn("#c9a84c", "#0b1e30", true), padding: "6px 14px", fontSize: "12px" }}>+ Add Stop</button>
      </div>

      {/* Route preview */}
      <div style={{ background: "#060f18", border: "1px solid #c9a84c22", borderRadius: "8px", padding: "10px 12px", marginBottom: "14px", fontSize: "11px", color: "#9ab8c8", lineHeight: 1.6 }}>
        <span style={{ color: "#c9a84c55", marginRight: "6px" }}>ROUTE</span>{routePreview}
      </div>

      {/* Edit / New form */}
      {editing && (
        <div style={{ ...S.card("#c9a84c22"), marginBottom: "14px" }}>
          <div style={{ ...S.label, marginBottom: "10px" }}>{editing === "new" ? "New Stop" : "Edit Stop"}</div>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {inp("name", "Stop name")}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
              {inp("lat", "Latitude e.g. 43.9530", "number")}
              {inp("lng", "Longitude e.g. -76.1113", "number")}
            </div>
            <select value={form.type || "landmark"} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
              style={{ background: "#08141f", border: "1px solid #c9a84c33", borderRadius: "6px", padding: "9px 10px", color: "#e8e0d0", fontFamily: "inherit", fontSize: "13px" }}>
              {TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <div>
              <div style={{ fontSize: "11px", color: "#9ab8c8", marginBottom: "6px" }}>Icon</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                {ICON_OPTIONS.map(ic => (
                  <button key={ic} onClick={() => setForm(f => ({ ...f, icon: ic }))}
                    style={{ width: "34px", height: "34px", borderRadius: "6px", border: `2px solid ${form.icon === ic ? "#c9a84c" : "transparent"}`, background: form.icon === ic ? "#c9a84c22" : "#08141f", fontSize: "18px", cursor: "pointer" }}>
                    {ic}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: "#9ab8c8" }}>
                <input type="checkbox" checked={form.active === 1} onChange={e => setForm(f => ({ ...f, active: e.target.checked ? 1 : 0 }))} />
                Active (visible to riders)
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: "#e8843a" }}>
                <input type="checkbox" checked={form.turnaround === 1} onChange={e => setForm(f => ({ ...f, turnaround: e.target.checked ? 1 : 0 }))} />
                ↩ Turnaround point (shuttle reverses here)
              </label>
            </div>
          </div>
          {err && <div style={{ fontSize: "12px", color: "#e8843a", marginTop: "8px" }}>{err}</div>}
          <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
            <button onClick={save} disabled={saving} style={{ ...S.btn("#c9a84c", "#0b1e30", true), flex: 1 }}>{saving ? "Saving…" : "Save"}</button>
            <button onClick={cancel} style={{ ...S.btn("#ffffff11", "#9ab8c8", true), flex: 1 }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Draggable stop list */}
      <div style={{ fontSize: "11px", color: "#5ba3a066", marginBottom: "6px" }}>Drag to reorder · tap ↩ to set turnaround</div>
      {localStops.map((s, idx) => (
        <div key={s.id}
          draggable
          onDragStart={() => onDragStart(idx)}
          onDragEnter={() => onDragEnter(idx)}
          onDragEnd={onDragEnd}
          onDragOver={e => e.preventDefault()}
          style={{ ...S.card(s.turnaround === 1 ? "#e8843a33" : undefined), marginBottom: "8px", display: "flex", alignItems: "center", gap: "10px", cursor: "grab", userSelect: "none" }}>
          <span style={{ fontSize: "16px", color: "#5ba3a055", flexShrink: 0 }}>⠿</span>
          <span style={{ fontSize: "18px", flexShrink: 0 }}>{s.icon || "📍"}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: "13px", fontWeight: 600, marginBottom: "2px" }}>
              {s.name}
              {s.turnaround === 1 && <span style={{ marginLeft: "6px", fontSize: "11px", color: "#e8843a", background: "#e8843a22", padding: "1px 6px", borderRadius: "10px" }}>↩ turnaround</span>}
            </div>
            <div style={{ fontSize: "11px", color: "#9ab8c8" }}>{s.lat.toFixed(4)}, {s.lng.toFixed(4)} · {s.type}</div>
          </div>
          <div style={{ display: "flex", gap: "5px", flexShrink: 0 }}>
            <button onClick={() => toggleTurnaround(s)}
              title="Toggle turnaround"
              style={{ background: s.turnaround === 1 ? "#e8843a44" : "#ffffff11", border: `1px solid ${s.turnaround === 1 ? "#e8843a" : "#ffffff22"}`, borderRadius: "6px", padding: "5px 8px", color: s.turnaround === 1 ? "#e8843a" : "#5ba3a0", fontSize: "13px", cursor: "pointer" }}>↩</button>
            <button onClick={() => startEdit(s)} style={{ background: "#c9a84c22", border: "1px solid #c9a84c44", borderRadius: "6px", padding: "5px 9px", color: "#c9a84c", fontSize: "12px", cursor: "pointer", fontFamily: "inherit" }}>Edit</button>
            <button onClick={() => deleteStop(s.id, s.name)} style={{ background: "#e8843a22", border: "1px solid #e8843a44", borderRadius: "6px", padding: "5px 9px", color: "#e8843a", fontSize: "12px", cursor: "pointer", fontFamily: "inherit" }}>Del</button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Admin Dashboard
// ─────────────────────────────────────────────────────────────────────────────
function AdminView({ requests, stops, shuttleLoc, onStopsChange }) {
  const [tab, setTab] = useState("stats");
  const [drivers, setDrivers] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [driverForm, setDriverForm] = useState({ name: "", pin: "" });
  const [editingDriver, setEditingDriver] = useState(null);
  const [driverErr, setDriverErr] = useState("");
  const bp = useBreakpoint();

  const loadDrivers = async () => {
    const d = await apiFetch("/drivers");
    if (d) setDrivers(d);
  };
  const loadShifts = async () => {
    const s = await apiFetch("/shifts");
    if (s) setShifts(s);
  };

  useEffect(() => { if (tab === "drivers") { loadDrivers(); loadShifts(); } }, [tab]);

  const saveDriver = async () => {
    if (!driverForm.name.trim()) { setDriverErr("Name required"); return; }
    if (!/^\d{6}$/.test(driverForm.pin)) { setDriverErr("PIN must be exactly 6 digits"); return; }
    const result = editingDriver
      ? await apiFetch(`/drivers/${editingDriver}`, { method: "PATCH", body: JSON.stringify(driverForm) })
      : await apiFetch("/drivers", { method: "POST", body: JSON.stringify(driverForm) });
    if (!result) { setDriverErr("PIN already in use or server error"); return; }
    setDriverForm({ name: "", pin: "" }); setEditingDriver(null); setDriverErr("");
    loadDrivers();
  };

  const toggleDriver = async (d) => {
    await apiFetch(`/drivers/${d.id}`, { method: "PATCH", body: JSON.stringify({ active: d.active === 1 ? 0 : 1 }) });
    loadDrivers();
  };

  const deleteDriver = async (d) => {
    if (!window.confirm(`Remove driver "${d.name}"?`)) return;
    await apiFetch(`/drivers/${d.id}`, { method: "DELETE" });
    loadDrivers();
  };

  const formatTime = (ts) => {
    if (!ts) return "—";
    return new Date(ts * 1000).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  };

  const stats = {
    total: requests.length,
    completed: requests.filter(r => r.status === "completed").length,
    pending: requests.filter(r => r.status === "pending").length,
    active: requests.filter(r => r.status === "active").length,
    riders: requests.reduce((a, r) => a + r.party, 0),
  };

  const statCols = bp === "sm" ? "1fr 1fr" : "repeat(4, 1fr)";
  const mapHeight = bp === "sm" ? "190px" : bp === "md" ? "260px" : "320px";
  const contentPad = bp === "sm" ? "16px 20px" : "20px 32px";

  return (
    <div style={{ minHeight: "100vh", background: "#08141f", color: "#e8e0d0", fontFamily: "'Source Serif 4', serif" }}>
      <header style={{ background: "#0d2136", borderBottom: "1px solid #c9a84c55", padding: bp === "sm" ? "16px 20px" : "16px 32px" }}>
        <div style={{ fontFamily: "'Playfair Display', serif", fontSize: "20px", fontWeight: 900, color: "#c9a84c" }}>Dashboard</div>
        <div style={{ fontSize: "10px", color: "#5ba3a0", letterSpacing: "0.12em", textTransform: "uppercase" }}>Harbor Shuttle · Today</div>
      </header>

      <div style={{ display: "flex", borderBottom: "1px solid #c9a84c22", background: "#0d2136" }}>
        {[["stats", "📊 Stats"], ["stops", "📍 Stops"], ["rides", "🛺 Rides"], ["drivers", "👤 Drivers"]].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} style={{ flex: 1, padding: "10px 4px", background: "none", border: "none", borderBottom: `2px solid ${tab === id ? "#c9a84c" : "transparent"}`, color: tab === id ? "#c9a84c" : "#5ba3a0", fontSize: "12px", cursor: "pointer", fontFamily: "inherit" }}>{label}</button>
        ))}
      </div>

      <div style={{ padding: contentPad }}>
        {tab === "stats" && <>
          <div style={{ display: "grid", gridTemplateColumns: statCols, gap: "10px", marginBottom: "18px" }}>
            {[["Total Rides", stats.total, "#c9a84c"], ["Completed", stats.completed, "#3ec6a0"], ["Pending", stats.pending, "#e8843a"], ["Riders Served", stats.riders, "#5ba3a0"]].map(([label, val, color]) => (
              <div key={label} style={{ ...S.card(), textAlign: "center" }}>
                <div style={{ fontFamily: "'Playfair Display', serif", fontSize: "32px", color, lineHeight: 1 }}>{val}</div>
                <div style={{ fontSize: "11px", color: "#9ab8c8", marginTop: "4px" }}>{label}</div>
              </div>
            ))}
          </div>
          <div style={{ height: mapHeight, borderRadius: "10px", overflow: "hidden", border: "1px solid #c9a84c22" }}>
            <MapboxMap stops={stops} requests={requests} shuttleLoc={shuttleLoc} height={mapHeight} />
          </div>
        </>}

        {tab === "stops" && <StopsEditor stops={stops} onStopsChange={onStopsChange} />}

        {tab === "rides" && <>
          <div style={{ ...S.label, marginBottom: "10px" }}>All Requests</div>
          {requests.length === 0 && <div style={{ fontSize: "13px", color: "#5ba3a055" }}>No requests yet today</div>}
          <div style={{ display: bp !== "sm" ? "grid" : "block", gridTemplateColumns: bp === "lg" ? "1fr 1fr" : "1fr", gap: "8px" }}>
            {[...requests].reverse().map(r => (
              <div key={r.id} style={{ ...S.card(r.status === "active" ? "#3ec6a044" : r.status === "completed" ? "#ffffff11" : "#c9a84c22"), marginBottom: bp === "sm" ? "8px" : 0 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "5px" }}>
                  <span style={{ fontSize: "12px", color: "#9ab8c8" }}>{r.phone}</span>
                  <span style={{ fontSize: "11px", padding: "2px 8px", borderRadius: "20px", background: r.status === "completed" ? "#3ec6a022" : r.status === "active" ? "#c9a84c22" : "#e8843a22", color: r.status === "completed" ? "#3ec6a0" : r.status === "active" ? "#c9a84c" : "#e8843a" }}>{r.status}</span>
                </div>
                <div style={{ fontSize: "13px", opacity: r.status === "completed" ? 0.5 : 1 }}>
                  {r.pickup.name} → {r.dropoff.name} · {r.party} pax
                </div>
              </div>
            ))}
          </div>
        </>}

        {tab === "drivers" && <>
          {/* Add / Edit form */}
          <div style={{ ...S.card("#c9a84c22"), marginBottom: "18px" }}>
            <div style={{ ...S.label, marginBottom: "10px" }}>{editingDriver ? "Edit Driver" : "Add Driver"}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <input placeholder="Driver name" value={driverForm.name} onChange={e => { setDriverForm(f => ({ ...f, name: e.target.value })); setDriverErr(""); }}
                style={{ background: "#08141f", border: "1px solid #c9a84c33", borderRadius: "6px", padding: "9px 10px", color: "#e8e0d0", fontFamily: "inherit", fontSize: "13px" }} />
              <input placeholder="6-digit PIN" maxLength={6} inputMode="numeric" value={driverForm.pin} onChange={e => { setDriverForm(f => ({ ...f, pin: e.target.value.replace(/\D/g, "").slice(0, 6) })); setDriverErr(""); }}
                style={{ background: "#08141f", border: `1px solid ${driverErr ? "#e8843a" : "#c9a84c33"}`, borderRadius: "6px", padding: "9px 10px", color: "#e8e0d0", fontFamily: "inherit", fontSize: "13px", letterSpacing: "0.2em" }} />
              {driverErr && <div style={{ fontSize: "12px", color: "#e8843a" }}>{driverErr}</div>}
              <div style={{ display: "flex", gap: "8px" }}>
                <button onClick={saveDriver} style={{ ...S.btn("#c9a84c", "#0b1e30", true), flex: 1 }}>{editingDriver ? "Save" : "Add Driver"}</button>
                {editingDriver && <button onClick={() => { setEditingDriver(null); setDriverForm({ name: "", pin: "" }); setDriverErr(""); }} style={{ ...S.btn("#ffffff11", "#9ab8c8", true), flex: 1 }}>Cancel</button>}
              </div>
            </div>
          </div>

          {/* Driver roster */}
          <div style={{ ...S.label, marginBottom: "8px" }}>Roster ({drivers.length})</div>
          {drivers.map(d => (
            <div key={d.id} style={{ ...S.card(d.active ? undefined : "#ffffff08"), marginBottom: "8px", display: "flex", alignItems: "center", gap: "10px", opacity: d.active ? 1 : 0.5 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: "13px", fontWeight: 600 }}>{d.name}</div>
                <div style={{ fontSize: "11px", color: "#9ab8c8", letterSpacing: "0.15em" }}>PIN: {d.pin}</div>
              </div>
              <button onClick={() => toggleDriver(d)} style={{ background: d.active ? "#3ec6a022" : "#ffffff11", border: `1px solid ${d.active ? "#3ec6a044" : "#ffffff22"}`, borderRadius: "6px", padding: "4px 8px", color: d.active ? "#3ec6a0" : "#9ab8c8", fontSize: "11px", cursor: "pointer" }}>
                {d.active ? "Active" : "Off"}
              </button>
              <button onClick={() => { setEditingDriver(d.id); setDriverForm({ name: d.name, pin: d.pin }); setDriverErr(""); }}
                style={{ background: "#c9a84c22", border: "1px solid #c9a84c44", borderRadius: "6px", padding: "4px 8px", color: "#c9a84c", fontSize: "11px", cursor: "pointer" }}>Edit</button>
              {d.id !== "d_admin" && <button onClick={() => deleteDriver(d)} style={{ background: "#e8843a22", border: "1px solid #e8843a44", borderRadius: "6px", padding: "4px 8px", color: "#e8843a", fontSize: "11px", cursor: "pointer" }}>Del</button>}
            </div>
          ))}

          {/* Shift log */}
          {shifts.length > 0 && <>
            <div style={{ ...S.label, margin: "18px 0 8px" }}>Recent Shifts</div>
            {shifts.map(s => (
              <div key={s.id} style={{ ...S.card(), marginBottom: "6px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: "13px", fontWeight: 600 }}>{s.driver_name}</div>
                  <div style={{ fontSize: "11px", color: "#9ab8c8" }}>{formatTime(s.started_at)}</div>
                </div>
                <div style={{ textAlign: "right", fontSize: "11px" }}>
                  {s.ended_at
                    ? <span style={{ color: "#3ec6a0" }}>Ended {formatTime(s.ended_at)}</span>
                    : <span style={{ color: "#e8843a" }}>● On shift</span>}
                </div>
              </div>
            ))}
          </>}
        </>}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Staff Login
// ─────────────────────────────────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [pin, setPin] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const attempt = async () => {
    if (pin.length !== 6) { setErr("PIN must be 6 digits"); return; }
    setLoading(true);
    const result = await apiFetch("/drivers/login", { method: "POST", body: JSON.stringify({ pin }) });
    setLoading(false);
    if (!result) { setErr("Incorrect PIN"); setPin(""); return; }
    onLogin(result); // { id, name, is_admin, shift_id }
  };

  const handleKey = e => { if (e.key === "Enter") attempt(); };

  return (
    <div style={{ minHeight: "100vh", background: "#0b1e30", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: "'Source Serif 4', serif", padding: "24px" }}>
      <span style={{ fontSize: "48px", marginBottom: "10px" }}>🛺</span>
      <div style={{ fontFamily: "'Playfair Display', serif", fontSize: "26px", fontWeight: 900, color: "#c9a84c", marginBottom: "3px" }}>Harbor Shuttle</div>
      <div style={{ fontSize: "11px", color: "#5ba3a0", letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: "28px" }}>Driver Login</div>
      <div style={{ width: "100%", maxWidth: "280px" }}>
        <input
          type="password"
          inputMode="numeric"
          maxLength={6}
          placeholder="6-digit PIN"
          value={pin}
          onChange={e => { setPin(e.target.value.replace(/\D/g, "").slice(0, 6)); setErr(""); }}
          onKeyDown={handleKey}
          style={{ width: "100%", background: "#0d2136", border: `1px solid ${err ? "#e8843a" : "#c9a84c44"}`, borderRadius: "8px", padding: "14px", color: "#e8e0d0", fontFamily: "inherit", fontSize: "22px", textAlign: "center", letterSpacing: "0.4em", boxSizing: "border-box", marginBottom: "8px" }}
        />
        {err && <div style={{ fontSize: "12px", color: "#e8843a", textAlign: "center", marginBottom: "8px" }}>{err}</div>}
        <button onClick={attempt} disabled={loading || pin.length !== 6} style={{ ...S.btn("#c9a84c", "#0b1e30"), opacity: pin.length === 6 ? 1 : 0.4 }}>
          {loading ? "Checking…" : "Sign In"}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab bar
// ─────────────────────────────────────────────────────────────────────────────
function TabBar({ tabs, active, onChange }) {
  return (
    <div style={{ display: "flex", background: "#060f18", borderTop: "1px solid #c9a84c33", position: "sticky", bottom: 0 }}>
      {tabs.map(t => (
        <button key={t.id} onClick={() => onChange(t.id)} style={{ flex: 1, padding: "11px 4px 9px", background: "none", border: "none", color: active === t.id ? "#c9a84c" : "#5ba3a0", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: "3px" }}>
          <span style={{ fontSize: "17px" }}>{t.icon}</span>
          <span style={{ fontSize: "10px", letterSpacing: "0.06em", fontFamily: "'Source Serif 4', serif" }}>{t.label}</span>
          {active === t.id && <div style={{ width: "18px", height: "2px", background: "#c9a84c", borderRadius: "2px" }} />}
        </button>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Root App — polls API, manages state
// ─────────────────────────────────────────────────────────────────────────────
export default function App() {
  const [requests, setRequests] = useState([]);
  const [stops, setStops] = useState([]);
  const [shuttleLoc, setShuttleLoc] = useState({ lat: 43.9505, lng: -76.1160 });
  const [driver, setDriver] = useState(null);   // { id, name, is_admin, shift_id }
  const [staffTab, setStaffTab] = useState("driver");
  const [publicTab, setPublicTab] = useState("request");
  const gpsIntervalRef = useRef(null);

  // Load fonts, Mapbox GL JS + CSS, hide attribution — all once at app level
  useEffect(() => {
    const fontLink = document.createElement("link");
    fontLink.rel = "stylesheet";
    fontLink.href = "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&family=Source+Serif+4:ital,wght@0,300;0,400;0,600;1,300&display=swap";
    document.head.appendChild(fontLink);

    const style = document.createElement("style");
    style.textContent = ".mapboxgl-ctrl-attrib,.mapboxgl-ctrl-logo{display:none!important}";
    document.head.appendChild(style);

    // Load Mapbox once globally — never removed so markers always have their CSS
    if (!document.querySelector('script[src*="mapbox-gl.js"]')) {
      const mboxCss = document.createElement("link");
      mboxCss.rel = "stylesheet";
      mboxCss.href = "https://api.mapbox.com/mapbox-gl-js/v3.3.0/mapbox-gl.css";
      document.head.appendChild(mboxCss);

      const mboxJs = document.createElement("script");
      mboxJs.src = "https://api.mapbox.com/mapbox-gl-js/v3.3.0/mapbox-gl.js";
      document.head.appendChild(mboxJs);
    }
  }, []);

  const loadStops = async () => {
    const data = await apiFetch("/stops");
    if (data) setStops(data);
  };

  // Load stops on mount
  useEffect(() => { loadStops(); }, []);

  // Poll shuttle location every 5s
  useEffect(() => {
    const poll = async () => {
      const data = await apiFetch("/shuttle/location");
      if (data?.lat && data?.lng) setShuttleLoc(data);
    };
    poll();
    const id = setInterval(poll, 5000);
    return () => clearInterval(id);
  }, []);

  // Poll requests every 10s (when driver logged in)
  useEffect(() => {
    if (!driver) return;
    const poll = async () => {
      const data = await apiFetch("/requests");
      if (data) setRequests(data);
    };
    poll();
    const id = setInterval(poll, 10000);
    return () => clearInterval(id);
  }, [driver]);

  const submitRequest = async (req) => {
    const newReq = { id: `r${Date.now()}`, ...req, status: "pending", created_at: Date.now() };
    const body = {
      pickup_id: req.pickup.id,
      dropoff_id: req.dropoff.id,
      phone: req.phone,
      party: req.party,
      ...(req.pickup.isPin && { pickup_lat: req.pickup.lat, pickup_lng: req.pickup.lng, pickup_name: req.pickup.name }),
      ...(req.dropoff.isPin && { dropoff_lat: req.dropoff.lat, dropoff_lng: req.dropoff.lng, dropoff_name: req.dropoff.name }),
    };
    const result = await apiFetch("/requests", { method: "POST", body: JSON.stringify(body) });
    setRequests(prev => [...prev, result || newReq]);
  };

  const updateRequest = async (id, status) => {
    setRequests(prev => prev.map(r => r.id === id ? { ...r, status } : r));
    await apiFetch(`/requests/${id}`, { method: "PATCH", body: JSON.stringify({ status }) });
  };

  // GPS broadcast from driver device
  const handleBroadcastLocation = (on) => {
    if (on) {
      gpsIntervalRef.current = setInterval(() => {
        navigator.geolocation.getCurrentPosition(pos => {
          const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          setShuttleLoc(loc);
          apiFetch("/shuttle/location", { method: "POST", body: JSON.stringify(loc) });
        });
      }, 4000);
    } else {
      clearInterval(gpsIntervalRef.current);
    }
  };

  const exitDriver = async () => {
    clearInterval(gpsIntervalRef.current);
    if (driver?.id) await apiFetch("/drivers/end-shift", { method: "POST", body: JSON.stringify({ driver_id: driver.id }) });
    setDriver(null);
    setStaffTab("driver");
    setPublicTab("request");
  };

  // Staff portal
  if (driver) {
    const tabs = driver.is_admin
      ? [{ id: "driver", icon: "🛺", label: "Rides" }, { id: "dashboard", icon: "📊", label: "Dashboard" }]
      : null; // driver-only: no tab bar needed

    return (
      <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", position: "relative" }}>
        <div style={{ flex: 1, overflowY: "auto" }}>
          {(staffTab === "driver" || !driver.is_admin) && (
            <DriverView requests={requests} stops={stops} shuttleLoc={shuttleLoc} onUpdate={updateRequest} onBroadcastLocation={handleBroadcastLocation} driverName={driver.name} />
          )}
          {staffTab === "dashboard" && driver.is_admin && (
            <AdminView requests={requests} stops={stops} shuttleLoc={shuttleLoc} onStopsChange={loadStops} />
          )}
        </div>
        {tabs && <TabBar tabs={tabs} active={staffTab} onChange={setStaffTab} />}
        <button onClick={exitDriver} style={{ position: "absolute", top: "14px", right: "16px", background: "none", border: "none", color: "#5ba3a0", fontSize: "12px", cursor: "pointer", fontFamily: "inherit" }}>Exit ×</button>
      </div>
    );
  }

  // Public
  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <div style={{ flex: 1 }}>
        {publicTab === "request" && <PublicView requests={requests} stops={stops} shuttleLoc={shuttleLoc} onSubmit={submitRequest} />}
        {publicTab === "map" && (
          <div style={{ background: "#0b1e30", minHeight: "100vh", display: "flex", flexDirection: "column" }}>
            <div style={{ padding: "20px 22px 14px", borderBottom: "1px solid #c9a84c22", flexShrink: 0 }}>
              <div style={{ fontFamily: "'Playfair Display', serif", fontSize: "20px", color: "#c9a84c" }}>Live Map</div>
              <div style={{ fontSize: "12px", color: "#5ba3a0" }}>Shuttle & stop locations</div>
            </div>
            <div style={{ flex: 1 }}>
              <MapboxMap stops={stops} requests={requests} shuttleLoc={shuttleLoc} height="100%" />
            </div>
          </div>
        )}
        {publicTab === "staff" && <LoginScreen onLogin={(d) => { setDriver(d); setStaffTab("driver"); }} />}
      </div>
      <TabBar
        tabs={[{ id: "request", icon: "🛺", label: "Ride" }, { id: "map", icon: "🗺️", label: "Map" }, { id: "staff", icon: "🔑", label: "Staff" }]}
        active={publicTab} onChange={setPublicTab}
      />
    </div>
  );
}
